import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SearchIcon, ArrowUpDownIcon, FileTextIcon, ClockIcon } from 'lucide-react';
import { loanFiles, mockData, stageColors } from './data';
interface FilesDrilldownProps {
  metricId: string | null;
  onClose: () => void;
}
type SortKey = 'name' | 'amount' | 'days' | 'points';
export function FilesDrilldown({
  metricId,
  onClose
}: FilesDrilldownProps) {
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState<string>('all');
  const [sortKey, setSortKey] = useState<SortKey>('points');
  const [sortDesc, setSortDesc] = useState(true);
  // Reset filters whenever a new metric opens
  useEffect(() => {
    if (metricId) {
      setSearch('');
      setStageFilter('all');
      setSortKey('points');
      setSortDesc(true);
    }
  }, [metricId]);
  const metric = mockData.performance.find(m => m.id === metricId);
  const matchingFiles = useMemo(() => {
    if (!metricId) return [];
    let files = loanFiles.filter(f => f.metrics.includes(metricId));
    if (search.trim()) {
      const q = search.toLowerCase();
      files = files.filter(f => f.borrowerName.toLowerCase().includes(q) || f.loanNumber.toLowerCase().includes(q));
    }
    if (stageFilter !== 'all') {
      files = files.filter(f => f.stage === stageFilter);
    }
    files = [...files].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'name':
          cmp = a.borrowerName.localeCompare(b.borrowerName);
          break;
        case 'amount':
          cmp = a.loanAmount - b.loanAmount;
          break;
        case 'days':
          cmp = a.daysInStage - b.daysInStage;
          break;
        case 'points':
          cmp = (a.pointsByMetric[metricId] || 0) - (b.pointsByMetric[metricId] || 0);
          break;
      }
      return sortDesc ? -cmp : cmp;
    });
    return files;
  }, [metricId, search, stageFilter, sortKey, sortDesc]);
  const stagesPresent = useMemo(() => {
    if (!metricId) return [];
    const stages = new Set<string>();
    loanFiles.filter(f => f.metrics.includes(metricId)).forEach(f => stages.add(f.stage));
    return Array.from(stages);
  }, [metricId]);
  const totalPoints = matchingFiles.reduce((sum, f) => sum + (f.pointsByMetric[metricId || ''] || 0), 0);
  const totalAmount = matchingFiles.reduce((sum, f) => sum + f.loanAmount, 0);
  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDesc(!sortDesc);else {
      setSortKey(key);
      setSortDesc(true);
    }
  };
  const fmtMoney = (n: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(n);
  return <AnimatePresence data-id="element-150">
      {metricId && metric && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/40 z-40" data-id="element-151" />
          <motion.aside initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'tween',
        duration: 0.3,
        ease: 'easeOut'
      }} className="fixed top-0 right-0 bottom-0 w-full max-w-3xl bg-white z-50 flex flex-col" role="dialog" aria-label={`${metric.title} files`} data-id="element-152">
            {/* Header */}
            <div className={`${metric.color} text-white p-5 shrink-0`} data-id="element-153">
              <div className="flex items-start justify-between mb-4" data-id="element-154">
                <div className="min-w-0" data-id="element-155">
                  <div className="text-[10px] font-bold uppercase tracking-widest opacity-80 mb-1" data-id="element-156">
                    Drill-down · Files contributing
                  </div>
                  <h2 className="text-xl font-bold uppercase tracking-wide truncate" data-id="element-157">
                    {metric.title}
                  </h2>
                  <p className="text-xs font-medium opacity-90 uppercase tracking-wider" data-id="element-158">
                    {metric.subtitle}
                  </p>
                </div>
                <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/15 transition-colors shrink-0" aria-label="Close drill-down" data-id="element-159">
                  <XIcon className="w-5 h-5" data-id="element-160" />
                </button>
              </div>

              <div className="grid grid-cols-3 gap-3" data-id="element-161">
                <div className="bg-white/15 rounded-lg p-3" data-id="element-162">
                  <div className="text-[10px] font-bold uppercase opacity-80 mb-1 tracking-wider" data-id="element-163">
                    Files
                  </div>
                  <div className="text-2xl font-bold tabular-nums" data-id="element-164">
                    {matchingFiles.length}
                  </div>
                </div>
                <div className="bg-white/15 rounded-lg p-3" data-id="element-165">
                  <div className="text-[10px] font-bold uppercase opacity-80 mb-1 tracking-wider" data-id="element-166">
                    Points
                  </div>
                  <div className="text-2xl font-bold tabular-nums" data-id="element-167">
                    {totalPoints > 0 ? '+' : ''}
                    {totalPoints.toLocaleString()}
                  </div>
                </div>
                <div className="bg-white/15 rounded-lg p-3" data-id="element-168">
                  <div className="text-[10px] font-bold uppercase opacity-80 mb-1 tracking-wider" data-id="element-169">
                    Volume
                  </div>
                  <div className="text-2xl font-bold tabular-nums truncate" data-id="element-170">
                    {fmtMoney(totalAmount)}
                  </div>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="border-b border-gray-200 p-4 bg-gray-50 flex flex-col md:flex-row gap-3 shrink-0" data-id="element-171">
              <div className="relative flex-1" data-id="element-172">
                <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-173" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search borrower or loan #" className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#0A1F5C]/20 focus:border-[#0A1F5C]" data-id="element-174" />
              </div>
              <select value={stageFilter} onChange={e => setStageFilter(e.target.value)} className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white font-medium text-[#0A1F5C] focus:outline-none focus:ring-2 focus:ring-[#0A1F5C]/20 focus:border-[#0A1F5C]" data-id="element-175">
                <option value="all" data-id="element-176">All stages ({matchingFiles.length})</option>
                {stagesPresent.map(s => <option key={s} value={s} data-id="element-177">
                    {s}
                  </option>)}
              </select>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-y-auto" data-id="element-178">
              {matchingFiles.length === 0 ? <div className="flex flex-col items-center justify-center h-full text-gray-400 p-8" data-id="element-179">
                  <FileTextIcon className="w-12 h-12 mb-3" data-id="element-180" />
                  <p className="font-semibold text-sm" data-id="element-181">
                    No files match these filters
                  </p>
                  <button onClick={() => {
              setSearch('');
              setStageFilter('all');
            }} className="mt-3 text-xs font-bold text-[#2563EB] uppercase tracking-wider" data-id="element-182">
                    Clear filters
                  </button>
                </div> : <table className="w-full text-sm" data-id="element-183">
                  <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10" data-id="element-184">
                    <tr data-id="element-185">
                      <SortHeader label="Borrower" active={sortKey === 'name'} desc={sortDesc} onClick={() => toggleSort('name')} data-id="element-186" />
                      <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-187">
                        Stage
                      </th>
                      <SortHeader label="Amount" active={sortKey === 'amount'} desc={sortDesc} onClick={() => toggleSort('amount')} align="right" data-id="element-188" />
                      <SortHeader label="Days" active={sortKey === 'days'} desc={sortDesc} onClick={() => toggleSort('days')} align="right" data-id="element-189" />
                      <SortHeader label="Points" active={sortKey === 'points'} desc={sortDesc} onClick={() => toggleSort('points')} align="right" data-id="element-190" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100" data-id="element-191">
                    {matchingFiles.map(f => <tr key={f.id} className="hover:bg-gray-50 transition-colors" data-id="element-192">
                        <td className="px-4 py-3" data-id="element-193">
                          <div className="font-semibold text-[#0A1F5C] text-sm" data-id="element-194">
                            {f.borrowerName}
                          </div>
                          <div className="text-[10px] text-gray-500 font-mono" data-id="element-195">
                            {f.loanNumber} · {f.loanType}
                          </div>
                        </td>
                        <td className="px-4 py-3" data-id="element-196">
                          <span className={`inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded border tracking-wider ${stageColors[f.stage]}`} data-id="element-197">
                            {f.stage}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums font-semibold text-[#0A1F5C]" data-id="element-198">
                          {fmtMoney(f.loanAmount)}
                        </td>
                        <td className="px-4 py-3 text-right" data-id="element-199">
                          <div className="flex items-center justify-end gap-1 text-xs" data-id="element-200">
                            <ClockIcon className={`w-3 h-3 ${f.daysInStage > 5 ? 'text-red-500' : 'text-gray-400'}`} data-id="element-201" />
                            <span className={`tabular-nums font-bold ${f.daysInStage > 5 ? 'text-red-600' : 'text-[#0A1F5C]'}`} data-id="element-202">
                              {f.daysInStage}d
                            </span>
                          </div>
                          <div className="text-[10px] text-gray-500" data-id="element-203">
                            {f.lastTouched}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right" data-id="element-204">
                          <span className={`text-base font-bold tabular-nums ${metric.textColor}`} data-id="element-205">
                            {(f.pointsByMetric[metricId] || 0) > 0 ? '+' : ''}
                            {(f.pointsByMetric[metricId] || 0).toLocaleString()}
                          </span>
                        </td>
                      </tr>)}
                  </tbody>
                </table>}
            </div>

            {/* Footer summary */}
            <div className="border-t border-gray-200 bg-gray-50 px-5 py-3 flex justify-between items-center text-xs shrink-0" data-id="element-206">
              <span className="font-bold text-gray-500 uppercase tracking-wider" data-id="element-207">
                Showing {matchingFiles.length} files
              </span>
              <span className="font-bold text-[#0A1F5C] uppercase tracking-wider" data-id="element-208">
                Total:{' '}
                <span className={metric.textColor} data-id="element-209">
                  {totalPoints > 0 ? '+' : ''}
                  {totalPoints.toLocaleString()} pts
                </span>
              </span>
            </div>
          </motion.aside>
        </>}
    </AnimatePresence>;
}
function SortHeader({
  label,
  active,
  desc,
  onClick,
  align = 'left'
}: {
  label: string;
  active: boolean;
  desc: boolean;
  onClick: () => void;
  align?: 'left' | 'right';
}) {
  return <th onClick={onClick} className={`px-4 py-2.5 text-${align} text-[10px] font-bold uppercase tracking-wider cursor-pointer select-none transition-colors ${active ? 'text-[#0A1F5C]' : 'text-gray-500 hover:text-[#0A1F5C]'}`} data-id="element-210">
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`} data-id="element-211">
        {label}
        <ArrowUpDownIcon className={`w-3 h-3 ${active ? 'opacity-100' : 'opacity-30'}`} data-id="element-212" />
      </span>
    </th>;
}